import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const Login = () => {
  const [searchParams] = useSearchParams();
  const tipo = searchParams.get('tipo') || 'admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Password change flow
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const isAdmin = tipo === 'admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, isAdmin ? password : (password || ''));
      if (user.debe_cambiar_password) {
        setMustChangePassword(true);
        setLoading(false);
        return;
      }
      if (user.rol === 'admin') {
        navigate('/admin');
      } else {
        navigate('/representante');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setChangingPassword(true);

    try {
      await authAPI.cambiarPassword('', newPassword);
      navigate('/representante');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  // Password change screen
  if (mustChangePassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-lsfc-green-600 via-lsfc-green-700 to-lsfc-green-800">
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-yellow-100">
              <span className="text-4xl">🔐</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Crear Contraseña</h1>
            <p className="text-gray-500 text-sm mt-2">
              Es tu primer ingreso. Por seguridad, debes crear una contraseña.
            </p>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nueva Contraseña
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lsfc-green-500 focus:border-transparent"
                placeholder="Mínimo 6 caracteres"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Contraseña
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lsfc-green-500 focus:border-transparent"
                placeholder="Repita la contraseña"
              />
            </div>

            <button
              type="submit"
              disabled={changingPassword}
              className="w-full py-3 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 bg-gradient-to-r from-lsfc-green-600 to-lsfc-green-700"
            >
              {changingPassword ? 'Guardando...' : 'Crear Contraseña'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isAdmin
        ? 'bg-gradient-to-br from-lsfc-blue-600 via-lsfc-blue-700 to-lsfc-blue-800'
        : 'bg-gradient-to-br from-lsfc-green-600 via-lsfc-green-700 to-lsfc-green-800'
    }`}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 text-sm">
          <span>←</span>
          <span>Volver al inicio</span>
        </Link>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
            isAdmin ? 'bg-lsfc-blue-100' : 'bg-lsfc-green-100'
          }`}>
            <span className="text-4xl">{isAdmin ? '👨‍💼' : '👨‍👧‍👦'}</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            {isAdmin ? 'Panel Administrativo' : 'Portal de Representantes'}
          </h1>
          <p className="text-gray-500">Los Salias FC</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isAdmin ? 'Correo Electrónico' : 'Cédula'}
            </label>
            <input
              type={isAdmin ? 'email' : 'text'}
              value={email}
              onChange={(e) => setEmail(isAdmin ? e.target.value : e.target.value.toUpperCase())}
              required
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                isAdmin ? 'focus:ring-lsfc-blue-500' : 'focus:ring-lsfc-green-500'
              }`}
              placeholder={isAdmin ? 'admin@lsfc.com' : 'V12345678'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={isAdmin}
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                isAdmin ? 'focus:ring-lsfc-blue-500' : 'focus:ring-lsfc-green-500'
              }`}
              placeholder={isAdmin ? '••••••••' : 'Déjelo en blanco si es la primera vez'}
            />
            {!isAdmin && (
              <p className="text-xs text-gray-400 mt-1">Si es tu primer ingreso, deja la contraseña en blanco</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 ${
              isAdmin
                ? 'bg-gradient-to-r from-lsfc-blue-600 to-lsfc-blue-700 hover:from-lsfc-blue-700 hover:to-lsfc-blue-800'
                : 'bg-gradient-to-r from-lsfc-green-600 to-lsfc-green-700 hover:from-lsfc-green-700 hover:to-lsfc-green-800'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Iniciando...
              </span>
            ) : (
              'Iniciar Sesión'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center space-y-3">
          {!isAdmin && (
            <p className="text-gray-500 text-sm">
              ¿No tienes cuenta?{' '}
              <Link to="/inscripcion" className="text-lsfc-green-600 hover:underline font-medium">
                Inscribe a tu jugador
              </Link>
            </p>
          )}
          <p className="text-gray-400 text-xs">
            {isAdmin ? (
              <Link to="/login?tipo=representante" className="hover:text-gray-600">
                ¿Eres representante? Accede aquí →
              </Link>
            ) : (
              <Link to="/login?tipo=admin" className="hover:text-gray-600">
                ¿Eres administrador? Accede aquí →
              </Link>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
